<script setup>
    import SgaStepHeader from './SgaStepHeader.vue';

    const props = defineProps({
        profiles: {
            type: Array,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: null
        },
        isAuthenticated: {
            type: Boolean,
            required: true
        },
        selectedProfileId: {
            type: String,
            default: null
        }
    });

    const emit = defineEmits(['select', 'login-required']);

    /**
     * Mask the DNI/Identification value.
     * If the string has fewer than 5 characters, display unmasked.
     * Otherwise, replace all but the last 5 characters with asterisks.
     * Example: '48618776L' → '****8776L'
     */
    function maskDni(id) {
        if (!id) return '—';
        const str = String(id);
        if (str.length < 5) {
            return str;
        }
        return '*'.repeat(str.length - 5) + str.slice(-5);
    }

    /**
     * Check if a location badge should be rendered.
     * Returns false if city and state are both missing or empty.
     */
    function hasLocation(profile) {
        return profile.address?.city || profile.address?.state;
    }

    /**
     * Format location string from profile address.
     */
    function formatLocation(profile) {
        const city = profile.address?.city || '';
        const state = profile.address?.state || '';
        return [city, state].filter(Boolean).join(', ');
    }

    function handleSelect(profile) {
        emit('select', profile);
    }

    function handleLoginCta() {
        emit('login-required');
    }

    function isSelected(profileId) {
        return props.selectedProfileId === profileId;
    }
</script>

<template>
    <b-container class="seleccionar-perfil-sga">
        <!-- Rama 1: Usuario NO autenticado → Mostrar CTA de login -->
        <div v-if="!isAuthenticated && !loading" class="login-cta-container">
            <SgaStepHeader
                :title="$t('front.sga.checkout.perfil_login_titulo')"
                :description="$t('front.sga.checkout.perfil_login_descripcion')"
            />
            <div class="alert alert-info tw:border-l-4 tw:border-blue-500 tw:p-4 tw:mb-4">
                <p class="tw:text-sm tw:font-medium tw:mb-3">{{ $t('front.sga.login_required') }}</p>
                <p class="tw:text-xs tw:text-gray-600 tw:mb-4">{{ $t('front.sga.login_description') }}</p>
                <button 
                    type="button"
                    class="btn btn-primary"
                    @click="handleLoginCta"
                >
                    {{ $t('front.sga.go_to_login') }}
                </button>
            </div>
        </div>
        
        <!-- Rama 2: Usuario autenticado → Mostrar perfiles -->
        <div v-else-if="isAuthenticated">
            <SgaStepHeader
                :title="$t('front.sga.checkout.perfil_titulo')"
                :description="$t('front.sga.checkout.perfil_descripcion')"
            />
            <!-- Loading State: Skeleton Cards -->
            <div v-if="loading" class="tw:grid tw:grid-cols-1 tw:gap-6 md:tw:grid-cols-2">
                <div 
                    v-for="i in 3" 
                    :key="`skeleton-${i}`"
                    data-test-skeleton
                    class="
                        tw:animate-pulse tw:flex tw:flex-col tw:gap-3
                        tw:p-6 tw:rounded-2xl tw:bg-gray-100
                        tw:border-2 tw:border-gray-200
                    "
                >
                    <div class="tw:h-12 tw:w-12 tw:rounded-full tw:bg-gray-300"></div>
                    <div class="tw:h-4 tw:w-32 tw:bg-gray-300 tw:rounded"></div>
                    <div class="tw:h-3 tw:w-24 tw:bg-gray-300 tw:rounded"></div>
                    <div class="tw:h-3 tw:w-20 tw:bg-gray-300 tw:rounded tw:mt-2"></div>
                </div>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger tw:p-4 tw:rounded-lg tw:bg-red-50 tw:border-l-4 tw:border-red-600">
                <p class="tw:text-red-800">{{ error }}</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="profiles.length === 0" class="tw:p-12 tw:text-center">
                <div data-test-empty-state class="tw:rounded-2xl tw:border-2 tw:border-dashed tw:border-gray-300 tw:p-8 tw:bg-gray-50">
                    <svg xmlns="http://www.w3.org/2000/svg" class="tw:w-12 tw:h-12 tw:text-gray-400 tw:mx-auto tw:mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <p class="tw:text-gray-600 tw:text-sm">{{ $t('front.sga.no_profiles_found') }}</p>
                </div>
            </div>

            <!-- Profile Grid: Identity Pass Cards -->
            <div v-else class="tw:grid tw:grid-cols-1 tw:gap-6 md:tw:grid-cols-2">
                <div 
                    v-for="profile in profiles" 
                    :key="profile.sga_profile_id"
                    data-test-profile-item
                    :class="{
                        'tw:ring-2 tw:ring-offset-2 tw:ring-[var(--color-site)]': isSelected(profile.sga_profile_id),
                        'tw:border-gray-100 tw:shadow-sm': !isSelected(profile.sga_profile_id)
                    }"
                    class="
                        tw:relative tw:flex tw:flex-col tw:gap-3
                        tw:p-6 tw:rounded-2xl tw:bg-white
                        tw:border-2 tw:cursor-pointer
                        tw:transition-all tw:duration-300 tw:ease-out
                        tw:hover:-translate-y-0.5 tw:hover:shadow-md tw:hover:border-gray-200
                    "
                    @click="handleSelect(profile)"
                >
                    <!-- Selected Check Icon (top-right) -->
                    <div 
                        v-if="isSelected(profile.sga_profile_id)"
                        class="tw:absolute tw:top-3 tw:right-3 tw:transition-opacity tw:duration-200"
                    >
                        <div class="tw:flex tw:items-center tw:justify-center tw:w-6 tw:h-6 tw:rounded-full tw:bg-blue-500 tw:text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" class="tw:w-4 tw:h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                        </div>
                    </div>

                    <!-- Header: User Icon + Name -->
                    <div class="tw:flex tw:items-start tw:gap-3">
                        <!-- User Icon -->
                        <div class="tw:w-10 tw:h-10 tw:rounded-full tw:bg-blue-50 tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:mt-1">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class="tw:text-blue-500"
                            >
                                <circle cx="12" cy="8" r="4"/>
                                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                            </svg>
                        </div>
                        
                        <!-- Name -->
                        <div class="tw:flex-1">
                            <p class="tw:text-lg tw:font-bold tw:text-gray-900 tw:leading-tight">
                                {{ profile.profile?.name || profile.first_name }} {{ profile.profile?.lastName || profile.last_name }}
                            </p>
                        </div>
                    </div>

                    <!-- Contact Info (Email & Phone) -->
                    <div class="tw:flex tw:flex-col tw:gap-1 tw:mt-1">
                        <!-- Email -->
                        <div 
                            v-if="profile.profile?.email || profile.email"
                            class="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-gray-600"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" class="tw:w-3.5 tw:h-3.5 tw:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span class="tw:truncate">{{ profile.profile?.email || profile.email }}</span>
                        </div>
                        <!-- Phone -->
                        <div 
                            v-if="profile.profile?.phone || profile.phone"
                            class="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-gray-600"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" class="tw:w-3.5 tw:h-3.5 tw:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{{ profile.profile?.phone || profile.phone }}</span>
                        </div>
                    </div>

                    <!-- Masked Identification Line -->
                    <div class="tw:mt-auto tw:pt-3 tw:border-t tw:border-gray-100">
                        <p class="tw:font-mono tw:text-sm tw:text-gray-600 tw:tracking-wider">
                            {{ maskDni(profile.profile?.identification?.value || profile.identification?.value) }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </b-container>
</template>

<style scoped>
.seleccionar-perfil-sga {
    min-height: 200px;
}

/* Optional: Fine-tune hover shadow elevations */
@media (hover: hover) {
    [data-test-profile-item]:hover {
        transition: all 300ms ease-out;
    }
}
</style>
